import {useCallback, useMemo} from 'react';
import {
    View,
    Text,
    StyleSheet,
    FlatList,
    RefreshControl,
    useWindowDimensions,
    DimensionValue,
    ViewStyle
} from 'react-native';
import {Folder} from 'lucide-react-native';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {AppDataMediaFolder, AppDataMediaItem} from '@/data/app-data-interfaces';
import {ThemeType} from '@/theming/baseTheme';
import {MediaCard} from './MediaCard';
import {FolderCard} from './FolderCard';
import {ResponsiveGrid} from "@/components/common/ResponsiveGrid";
import {BaseCard} from "@/components/common/BaseCard";

interface FolderWithChildren extends AppDataMediaFolder {
    parentId?: string;
    children?: FolderWithChildren[];
}

interface MediaGridProps {
    folders?: FolderWithChildren[];
    mediaItems: AppDataMediaItem[];
    onFolderPress?: (folder: FolderWithChildren) => void;
    onMediaPress?: (item: AppDataMediaItem) => void;
    getFolderItemCount?: (folder: FolderWithChildren) => number;
    isRefreshing: boolean;
    onRefresh: () => void;
    scrollEnabled?: boolean;
    style?: ViewStyle;
}

export const MediaGrid = ({
                              folders = [],
                              mediaItems,
                              onFolderPress,
                              onMediaPress,
                              getFolderItemCount,
                              isRefreshing,
                              onRefresh,
                              scrollEnabled,
                              style
                          }: MediaGridProps) => {
    const {theme} = useCurrentTheme();
    const styles = createStyles(theme);
    const {width} = useWindowDimensions();

    console.log('[MediaGrid] Render with', {scrollEnabled});

    // Calculate number of columns based on screen width
    const numColumns = useMemo(() => {
        const maxItemWidth = 450; // Maximum width for a single item
        const minItemWidth = 250; // Minimum width for a single item
        const padding = 20; // Account for screen padding
        const availableWidth = width - padding;

        // Calculate columns ensuring items don't exceed max width
        let columns = Math.floor(availableWidth / minItemWidth);

        // Ensure item width doesn't exceed maxItemWidth
        const itemWidth = availableWidth / columns;
        if (itemWidth > maxItemWidth) {
            columns = Math.ceil(availableWidth / maxItemWidth);
        }

        // Minimum 2 columns for better layout
        return Math.max(2, columns);
    }, [width]);

    // Combine folders and media items
    const gridData = useMemo(() => {
        return [...folders, ...mediaItems];
    }, [folders, mediaItems]);

    // Calculate item width percentage based on columns
    const itemWidthPercentage = useMemo(() => {
        return `${100 / numColumns}%`;
    }, [numColumns]);

    // Render grid item
    const renderGridItem = useCallback((item: FolderWithChildren | AppDataMediaItem) => {
        if ('children' in item) {
            // It's a folder
            if (!onFolderPress || !getFolderItemCount) {
                return <View style={{display: 'none'}}/>;
            }

            const itemCount = getFolderItemCount(item);

            return (
                <FolderCard
                    folder={item}
                    itemCount={itemCount}
                    onPress={onFolderPress}
                />
            );
        } else {
            // It's a media item
            return (
                <MediaCard
                    item={item as AppDataMediaItem}
                    onPress={onMediaPress}
                />
            );
        }
    }, [getFolderItemCount, onFolderPress, onMediaPress]);

    if (gridData.length === 0) {
        return (
            <View style={[styles.emptyState, style]}>
                <Folder size={48} color={theme.colors.textSecondary} style={styles.emptyStateIcon}/>
                <Text style={styles.emptyStateText}>This folder is empty</Text>
            </View>
        );
    }

    return (
        <ResponsiveGrid
            minItemWidth={175}
            minColumns={2}
            data={gridData}
            renderItem={renderGridItem}
            keyExtractor={(item) => item.id}
            isRefreshing={isRefreshing}
            onRefresh={onRefresh}
            scrollEnabled={scrollEnabled}
            style={style}
        />
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    gridRow: {
        justifyContent: 'flex-start', // Changed from 'space-between' to keep consistent spacing
    },

    itemWrapper: {
        width: '50%', // Default for 2 columns, will be overridden by inline styles
        maxWidth: 450, // Maximum width for each card
        paddingHorizontal: 5,
    },

    emptyState: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: theme.spacing.lg,
    },

    emptyStateIcon: {
        opacity: 0.5,
        marginBottom: theme.spacing.md,
    },

    emptyStateText: {
        fontSize: theme.fontSize.md,
        color: theme.colors.textSecondary,
        textAlign: 'center',
    },
});
